import { mockPatients } from '@assets/mockdata/PatientTray/mockPatients'
import { getFullName } from '@utils/fullname.util'
import { isDashboardDevMocksEnabled } from '@utils/dashboardDevMocks.util'

/** Columns that identify a patient name cell. */
const PATIENT_NAME_COLUMN_KEYS = new Set(['patientname', 'patient_name'])

/** Provider, staff, or non-patient columns — never link to Patient Profile. */
const NON_PATIENT_COLUMN_KEYS = new Set([
  'provider',
  'providername',
  'hygienist',
  'operatory',
  'guarantor',
  'guarantorname',
  'lineitem',
  'role',
  'rank',
  'name',
  'patients',
  'patientsseen',
  'patientsdue',
  'patientscompleted',
  'patientsshare',
])

const NON_PATIENT_COLUMN_LABELS = new Set([
  'provider',
  'hygienist',
  'operatory',
  'guarantor',
  'patient collection',
  'patient share',
])

function normalizeName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '')
}

export function isDashboardPatientColumn(col: {
  key: string
  label: string
  linkToPatientProfile?: boolean
}): boolean {
  if (col.linkToPatientProfile === false) return false
  if (col.linkToPatientProfile === true) return true

  const key = col.key.toLowerCase()
  const label = col.label.trim().toLowerCase()

  if (NON_PATIENT_COLUMN_KEYS.has(key)) return false
  if (NON_PATIENT_COLUMN_LABELS.has(label)) return false

  return PATIENT_NAME_COLUMN_KEYS.has(key)
}

function resolveMockPatientIdByName(displayName: string): string {
  const normalized = normalizeName(displayName)

  for (const patient of mockPatients) {
    const fullName = normalizeName(getFullName(patient, true, false))
    if (fullName && fullName === normalized) {
      return patient.patient_id
    }

    const firstToken = normalizeName(patient.first_name.split(/\s+/)[0] ?? '')
    const lastName = normalizeName(patient.last_name)
    if (
      firstToken &&
      lastName &&
      normalized.includes(firstToken) &&
      normalized.includes(lastName)
    ) {
      return patient.patient_id
    }
  }

  let hash = 0
  for (let i = 0; i < displayName.length; i++) {
    hash = (hash + displayName.charCodeAt(i)) % mockPatients.length
  }
  return mockPatients[hash]?.patient_id ?? mockPatients[0].patient_id
}

function readPatientIdField(row: Record<string, unknown>): string | null {
  for (const key of ['patientId', 'patient_id', 'patientID']) {
    if (!(key in row)) continue
    const value = row[key]
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
    return null
  }
  return null
}

export function resolvePatientIdFromDashboardRow(
  row: Record<string, unknown>,
  displayName?: string,
): string | null {
  const fromRow = readPatientIdField(row)
  if (fromRow) return fromRow

  // Live API rows without patientId must not fall back to mock tray patients.
  if (!isDashboardDevMocksEnabled()) {
    return null
  }

  const name =
    displayName?.trim() ||
    (typeof row.patientName === 'string' ? row.patientName.trim() : '') ||
    (typeof row.patient === 'string' ? row.patient.trim() : '') ||
    (typeof row.patient_name === 'string' ? row.patient_name.trim() : '')

  if (!name) return null

  return resolveMockPatientIdByName(name)
}
